import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, HardDrive, Package, Truck, Wrench, RefreshCcw, Sparkles } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { STB_PAGE, STB_MODELS } from "@/lib/extendedContent";

const FULFILLMENT_ICONS = [Wrench, Package, Truck, RefreshCcw];

export default function STB() {
  const { lang, t } = useI18n();
  const p = pickLang(STB_PAGE, lang);
  const models = pickLang(STB_MODELS, lang);

  return (
    <div data-testid="stb-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline">{p.overline}</div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] text-white">
              {p.title_a} <br/><span className="gradient-text">{p.title_b}</span>
            </h1>
            <p className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed">{p.subtitle}</p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/demo" data-testid="stb-cta-demo" className="btn-primary">{t("cta.demo")} <ArrowRight size={18} /></Link>
              <Link to="/contact" data-testid="stb-cta-contact" className="btn-ghost">{t("cta.talk_sales")}</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl gradient-bg opacity-25 blur-3xl" />
              <div className="relative glass rounded-3xl p-6 md:p-8">
                <div className="aspect-[4/3] rounded-2xl bg-[#1A2236] relative overflow-hidden border border-white/10">
                  <div className="absolute inset-0 bg-grid opacity-40" />
                  {/* Stylized STB hardware mock */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className="w-56 h-32 md:w-72 md:h-40 rounded-2xl bg-gradient-to-br from-[#101521] to-[#1F2A3F] border border-white/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] flex items-center justify-center relative">
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#1FE0A8] pulse-dot" />
                            <span className="font-mono text-[9px] tracking-widest text-slate-400">P2S · 4K</span>
                          </div>
                          <span className="font-display text-[10px] text-slate-500">Push2Stream</span>
                        </div>
                        <HardDrive size={42} className="text-slate-500" />
                      </div>
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/40 blur-md rounded-full" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">P2S · Stream Max</div>
                  <div className="absolute top-4 right-4 px-2 py-1 rounded-full gradient-bg text-black font-mono text-[9px] uppercase tracking-widest font-bold">4K HDR</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODELS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10 md:mb-14 text-center">
            <div className="overline justify-center inline-flex">{p.models_overline}</div>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white max-w-3xl mx-auto leading-[1.05]">
              {p.models_title_a} <span className="gradient-text">{p.models_title_b}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {models.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`stb-model-${i}`}
                className={`relative rounded-3xl p-7 md:p-9 flex flex-col ${i === 1 ? "gradient-border" : "glass"}`}
                style={i === 1 ? { boxShadow: "0 30px 80px -20px rgba(31, 168, 255, 0.35)" } : {}}
              >
                {i === 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full gradient-bg text-black font-mono text-[10px] uppercase tracking-widest font-bold whitespace-nowrap">
                    {t("common.most_popular")}
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#1FE0A8]">{m.tier}</div>
                <h3 className="font-display mt-3 text-2xl md:text-3xl font-bold tracking-tight text-white">{m.name}</h3>
                <div className="mt-3 text-slate-200 text-sm">{m.price}</div>

                <ul className="mt-7 space-y-3 flex-1">
                  {m.specs.map((s, j) => (
                    <li key={j} className="flex gap-3 items-start text-sm text-slate-100">
                      <span className="mt-0.5 w-5 h-5 rounded-full gradient-bg flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} className="text-black" />
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 pt-5 border-t border-white/10">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Best for</div>
                  <div className="text-sm text-slate-200">{m.best_for}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FULFILLMENT */}
      <section className="py-20 md:py-28 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <div className="overline">{p.fulfillment_overline}</div>
              <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
                {p.fulfillment_title}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base md:text-lg text-slate-200 leading-relaxed">{p.fulfillment_subtitle}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.fulfillment_steps.map((step, i) => {
              const Icon = FULFILLMENT_ICONS[i] || Sparkles;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="glass rounded-2xl p-7 card-hover"
                  data-testid={`stb-step-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center text-black">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">0{i + 1}</div>
                  </div>
                  <h3 className="font-display mt-5 text-xl font-bold text-white tracking-tight">{step.title}</h3>
                  <p className="mt-3 text-sm text-slate-200 leading-relaxed">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            {p.cta_title_a} <span className="gradient-text">{p.cta_title_b}</span>
          </h2>
          <p className="mt-6 text-slate-200 max-w-2xl mx-auto">{p.cta_sub}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="stb-cta-demo-bottom" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="stb-cta-contact-bottom" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
